/**
 * Skin hook — premium visual themes (additive, non-destructive).
 *
 * `skin` is a theme id: 'classic' (original V3, default) or one of the premium
 * themes in premiumThemes.js (aurora / nebula / emerald / sunset / platinum).
 *
 * Persists to localStorage('v3_skin'), writes `data-skin` on <html>, and fires
 * a `v3-skin-change` window event so Layout.jsx can flip the route-gated
 * `body.v3-premium` + `data-pm-theme` instantly (Spatial Brief is excluded) and
 * so palette-aware charts can recolour live.
 *
 * Purely cosmetic — overrides CSS custom properties + restyles existing
 * primitives. No component markup or data changes; fully reversible at runtime.
 */
import { useState, useEffect, useCallback } from 'react';
import { ALL_SKINS } from '../premiumThemes';

const STORAGE_KEY = 'v3_skin';

// Aurora (premium blue·cyan, light) is the default look for new sessions.
const DEFAULT_SKIN = 'aurora';

function getInitialSkin() {
    const saved = localStorage.getItem(STORAGE_KEY);
    return ALL_SKINS.includes(saved) ? saved : DEFAULT_SKIN;
}

function applySkin(skin) {
    document.documentElement.setAttribute('data-skin', skin);
    window.dispatchEvent(new CustomEvent('v3-skin-change', { detail: skin }));
}

export function useSkin() {
    const [skin, setSkinState] = useState(getInitialSkin);

    useEffect(() => {
        // Write storage BEFORE dispatching so any listener that reads localStorage
        // sees the new value (Layout also reads event.detail as the primary source).
        localStorage.setItem(STORAGE_KEY, skin);
        applySkin(skin);
    }, [skin]);

    const setSkin = useCallback((id) => {
        setSkinState(ALL_SKINS.includes(id) ? id : 'classic');
    }, []);

    return { skin, setSkin, isPremium: skin !== 'classic' };
}
